import type { SyntheticEvent } from 'react';
import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import type { ReportMetric } from '../../../types/report.js';
import type { CampaignGroupOption } from '../../../runtimeConfig.js';

export type DatePresetOption = 'last7' | 'prev7' | 'last30' | 'prev30' | 'custom';

const PRESET_LABELS: Record<DatePresetOption, string> = {
  last7: 'Posledních 7 dní',
  prev7: 'Předchozích 7 dní',
  last30: 'Posledních 30 dní',
  prev30: 'Předchozích 30 dní',
  custom: 'Vlastní interval',
};

const formatDisplayDate = (input: string) => {
  if (!input) {
    return '—';
  }
  const date = new Date(`${input}T00:00:00.000Z`);
  return date.toLocaleDateString('cs-CZ', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export interface ReportFiltersProps {
  metric: ReportMetric;
  onMetricChange: (metric: ReportMetric) => void;
  campaignGroups: CampaignGroupOption[];
  selectedCampaignGroupId: string;
  onCampaignGroupChange: (campaignGroupId: string) => void;
  range: { from: string; to: string };
  onCustomRangeChange: (payload: { field: 'from' | 'to'; value: string }) => void;
  preset: DatePresetOption;
  onPresetChange: (preset: DatePresetOption) => void;
  validationError?: string;
  latestAllowedDate: string;
}

export const ReportFilters = ({
  metric,
  onMetricChange,
  campaignGroups,
  selectedCampaignGroupId,
  onCampaignGroupChange,
  range,
  onCustomRangeChange,
  preset,
  onPresetChange,
  validationError,
  latestAllowedDate,
}: ReportFiltersProps) => {
  const handleMetricChange = (_: SyntheticEvent, nextValue: ReportMetric | null) => {
    if (nextValue) {
      onMetricChange(nextValue);
    }
  };

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={3}
      alignItems={{ md: 'center' }}
      justifyContent="space-between"
    >
      <Stack spacing={1} flexShrink={0}>
        <Typography variant="subtitle2" color="text.secondary">
          Metoda výpočtu
        </Typography>
        <ToggleButtonGroup value={metric} exclusive onChange={handleMetricChange} size="small">
          <ToggleButton value="revenue" disableRipple>
            Revenue
          </ToggleButton>
          <ToggleButton value="trials" disableRipple>
            Trials
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack spacing={0.5} flexGrow={1}>
        {campaignGroups.length > 0 ? (
          <Typography variant="body2" color="text.secondary">
            Campaign group:{' '}
            {campaignGroups.find((group) => group.id === selectedCampaignGroupId)?.name ??
              'Nevybráno'}
          </Typography>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          Interval: {formatDisplayDate(range.from)} — {formatDisplayDate(range.to)} (včetně)
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Nejnovější dostupný den: {latestAllowedDate}
        </Typography>
      </Stack>

      <Stack spacing={2} flexBasis={{ xs: '100%', md: 360 }}>
        {campaignGroups.length > 0 ? (
          <FormControl size="small" fullWidth>
            <InputLabel id="campaign-group-label">Campaign group</InputLabel>
            <Select
              labelId="campaign-group-label"
              label="Campaign group"
              value={selectedCampaignGroupId}
              onChange={(event) => onCampaignGroupChange(event.target.value)}
            >
              {campaignGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <FormControl size="small" fullWidth>
          <InputLabel id="preset-label">Interval</InputLabel>
          <Select
            labelId="preset-label"
            label="Interval"
            value={preset}
            onChange={(event) => onPresetChange(event.target.value as DatePresetOption)}
          >
            {(Object.keys(PRESET_LABELS) as DatePresetOption[]).map((option) => (
              <MenuItem key={option} value={option}>
                {PRESET_LABELS[option]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {preset === 'custom' ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Od"
              type="date"
              value={range.from}
              onChange={(event) => onCustomRangeChange({ field: 'from', value: event.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              inputProps={{ max: latestAllowedDate }}
            />
            <TextField
              label="Do (včetně)"
              type="date"
              value={range.to}
              onChange={(event) => onCustomRangeChange({ field: 'to', value: event.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              inputProps={{ max: latestAllowedDate }}
            />
          </Stack>
        ) : null}

        {preset === 'custom' && validationError ? (
          <Alert severity="error" variant="outlined">
            {validationError}
          </Alert>
        ) : null}
      </Stack>
    </Stack>
  );
};
